import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import * as XLSX from "xlsx";

dayjs.extend(customParseFormat);

export type ParsedOfficeHolidayRow = {
  date: string;
  name: string;
  notes: string | null;
  /** 1-based row number in the original file (header is row 1). */
  sourceRowNumber: number;
};

export type OfficeHolidayImportParseError = {
  rowNumber: number;
  message: string;
};

/** One non-blank data row for the import preview table. */
export type OfficeHolidayPreviewRow = {
  rowNumber: number;
  /** Display / raw date text from the file (or normalized ISO when valid). */
  dateText: string;
  /** Parsed ISO date when valid; otherwise null. */
  date: string | null;
  name: string;
  notes: string | null;
  /** Blocking issues for this row (invalid date, missing name, superseded duplicate, …). */
  errors: string[];
};

export type ParseOfficeHolidaysImportResult = {
  /** Valid rows that will be sent on confirm (duplicate dates keep the last). */
  rows: ParsedOfficeHolidayRow[];
  /** All non-blank rows for preview (includes invalid / superseded). */
  previewRows: OfficeHolidayPreviewRow[];
  parseErrors: OfficeHolidayImportParseError[];
  skippedBlankRows: number;
};

const DATE_HEADER_KEYS = ["date", "holidaydate", "day", "holidayday"];
const NAME_HEADER_KEYS = ["name", "holiday", "holidayname", "title"];
const NOTES_HEADER_KEYS = ["notes", "note", "remarks", "remark", "comment", "description"];

const DATE_FORMATS = [
  "YYYY-MM-DD",
  "DD-MM-YYYY",
  "D-M-YYYY",
  "DD/MM/YYYY",
  "D/M/YYYY",
  "DD-MMM-YYYY",
  "D-MMM-YYYY",
  "DD MMM YYYY",
  "D MMM YYYY",
] as const;

function cellToString(value: unknown): string {
  if (value == null || value === "") return "";
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dayjs(value).format("YYYY-MM-DD");
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return Number.isInteger(value) ? String(value) : String(value);
  }
  return String(value).trim();
}

function normalizeHeaderKey(value: unknown): string {
  return cellToString(value)
    .replace(/^\uFEFF/, "")
    .toLowerCase()
    .replace(/[\s-_./]+/g, "");
}

function findColumnIndex(headers: unknown[], candidates: string[]): number {
  const normalized = headers.map((h) => normalizeHeaderKey(h));
  for (const key of candidates) {
    const idx = normalized.indexOf(key);
    if (idx >= 0) return idx;
  }
  return -1;
}

/** Parse flexible date cells (ISO, common Indian formats, Excel Date / serial). */
export function parseOfficeHolidayDateCell(value: unknown): string | null {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return dayjs(value).format("YYYY-MM-DD");
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed && parsed.y > 1900) {
      const m = String(parsed.m).padStart(2, "0");
      const d = String(parsed.d).padStart(2, "0");
      return `${parsed.y}-${m}-${d}`;
    }
  }

  const raw = cellToString(value);
  if (!raw) return null;

  for (const fmt of DATE_FORMATS) {
    const parsed = dayjs(raw, fmt, true);
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  }

  return null;
}

function sheetToGrid(sheet: XLSX.WorkSheet): unknown[][] {
  return XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    defval: "",
    raw: true,
  });
}

export function officeHolidayPreviewHasErrors(row: OfficeHolidayPreviewRow): boolean {
  return row.errors.length > 0;
}

export function countOfficeHolidayPreviewIssues(previewRows: OfficeHolidayPreviewRow[]): number {
  return previewRows.filter(officeHolidayPreviewHasErrors).length;
}

/** True when at least one row is ready to import. */
export function officeHolidayPreviewCanImport(result: ParseOfficeHolidaysImportResult): boolean {
  return result.rows.length > 0;
}

/**
 * Parse office-holiday CSV/XLSX grid. Requires date + name columns.
 * Duplicate dates keep the last valid row; earlier duplicates are marked in preview.
 */
export function parseOfficeHolidaysImportGrid(grid: unknown[][]): ParseOfficeHolidaysImportResult {
  if (grid.length < 2) {
    throw new Error("File must include a header row and at least one data row");
  }

  const headerRow = grid[0] ?? [];
  const dateCol = findColumnIndex(headerRow, DATE_HEADER_KEYS);
  if (dateCol < 0) {
    throw new Error(
      `No date column found. Use one of these headers: ${DATE_HEADER_KEYS.join(", ")}`
    );
  }
  const nameCol = findColumnIndex(headerRow, NAME_HEADER_KEYS);
  if (nameCol < 0) {
    throw new Error(
      `No name column found. Use one of these headers: ${NAME_HEADER_KEYS.join(", ")}`
    );
  }
  const notesCol = findColumnIndex(headerRow, NOTES_HEADER_KEYS);

  const previewRows: OfficeHolidayPreviewRow[] = [];
  let skippedBlankRows = 0;

  for (let i = 1; i < grid.length; i++) {
    const cells = grid[i] ?? [];
    const rowNumber = i + 1;
    const dateRaw = cells[dateCol];
    const nameRaw = cellToString(cells[nameCol] ?? "").trim();
    const notesRaw = notesCol >= 0 ? cellToString(cells[notesCol] ?? "").trim() || null : null;
    const dateText = cellToString(dateRaw);

    const hasAny =
      (dateRaw != null && String(dateRaw).trim() !== "") || nameRaw || (notesRaw ?? "");

    if (!hasAny) {
      skippedBlankRows += 1;
      continue;
    }

    const errors: string[] = [];
    const date = parseOfficeHolidayDateCell(dateRaw);
    if (!date) {
      errors.push(
        dateText ? `Invalid date "${dateText}" (use YYYY-MM-DD or DD-MM-YYYY)` : "Missing date"
      );
    }
    if (!nameRaw) {
      errors.push("Missing name");
    } else if (nameRaw.length > 255) {
      errors.push("Holiday name exceeds 255 characters");
    }

    previewRows.push({
      rowNumber,
      dateText: date ?? dateText,
      date,
      name: nameRaw,
      notes: notesRaw,
      errors,
    });
  }

  if (previewRows.length === 0) {
    throw new Error("No holiday rows found in file");
  }

  // Duplicate dates: keep the last valid row; mark earlier valid rows as superseded.
  const lastValidByDate = new Map<string, number>();
  for (const row of previewRows) {
    if (row.date && row.errors.length === 0) {
      lastValidByDate.set(row.date, row.rowNumber);
    }
  }
  for (const row of previewRows) {
    if (row.date && row.errors.length === 0 && lastValidByDate.get(row.date) !== row.rowNumber) {
      row.errors.push(
        `Duplicate date ${row.date} — later row ${lastValidByDate.get(row.date)} will be imported`
      );
    }
  }

  const rows: ParsedOfficeHolidayRow[] = previewRows
    .filter((r) => r.date && r.errors.length === 0)
    .map((r) => ({
      date: r.date!,
      name: r.name,
      notes: r.notes,
      sourceRowNumber: r.rowNumber,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  const parseErrors: OfficeHolidayImportParseError[] = previewRows
    .filter((r) => r.errors.length > 0)
    .flatMap((r) => r.errors.map((message) => ({ rowNumber: r.rowNumber, message })));

  return {
    rows,
    previewRows,
    parseErrors,
    skippedBlankRows,
  };
}

export function parseOfficeHolidaysWorkbook(wb: XLSX.WorkBook): ParseOfficeHolidaysImportResult {
  const sheetName = wb.SheetNames[0];
  if (!sheetName) {
    throw new Error("Workbook has no sheets");
  }
  const sheet = wb.Sheets[sheetName];
  if (!sheet) {
    throw new Error("Workbook has no sheets");
  }
  return parseOfficeHolidaysImportGrid(sheetToGrid(sheet));
}

/** Parse a CSV or XLSX file in the browser. */
export async function parseOfficeHolidaysImportFile(
  file: File
): Promise<ParseOfficeHolidaysImportResult> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  return parseOfficeHolidaysWorkbook(wb);
}

/** CSV template content for bulk office-holiday import. */
export const OFFICE_HOLIDAYS_CSV_TEMPLATE = [
  "date,name,notes",
  "2026-01-26,Republic Day,",
  "2026-08-15,Independence Day,National holiday",
  "2026-10-02,Gandhi Jayanti,",
].join("\n");

export function downloadOfficeHolidaysCsvTemplate(): void {
  const blob = new Blob([`${OFFICE_HOLIDAYS_CSV_TEMPLATE}\n`], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "office-holidays-import-template.csv";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Maps parsed holiday rows to POST /officeHolidays/bulk-import body shape. */
export function officeHolidayRowsToImportPayload(rows: ParsedOfficeHolidayRow[]) {
  return rows.map((r) => ({
    date: r.date,
    name: r.name,
    notes: r.notes,
    sourceRowNumber: r.sourceRowNumber,
  }));
}
